/**
 * PreferenciasCanais — 3 master toggles (in-app / email / push) global.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 #1 e §3.8 item 1.
 *
 * Cada toggle eh "master ON/OFF do channel" — backend deriva via
 * `subscriptions.some(s.channel===X && s.enabled===true)`. Toggle OFF →
 * PATCH com `enabled=false` para todas as subscriptions desse channel
 * (por source). Toggle ON → idem com enabled=true.
 */
import { Stack, Group, ThemeIcon, Text, Switch, Loader, Skeleton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBellRinging } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  invalidateMeNotificationQueries,
  notificationPreferencesApi,
  type SubscriptionChannel,
  type SubscriptionUpdate,
} from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { Callout, WidgetCard } from '@/components/ui'
import {
  PREFERENCE_MASTER_CHANNELS,
  PREFERENCE_SOURCES,
} from './preferencias-constants'

const QUERY_KEY = ['me', 'notification-subscriptions'] as const

export function PreferenciasCanais() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationPreferencesApi.listSubscriptions(),
  })

  // Master ON pra um channel = qualquer subscription daquele channel com enabled=true
  const masters = useMemo(() => {
    const map: Record<SubscriptionChannel, boolean> = {
      feed: false,
      inapp: false,
      email: false,
      webpush: false,
      whatsapp: false,
      slack: false,
      teams: false,
      sms: false,
      mobile: false,
      voice: false,
      robo: false,
    }
    if (!data?.items) return map
    for (const s of data.items) {
      if (s.enabled) map[s.channel] = true
    }
    return map
  }, [data])

  const mutation = useMutation({
    mutationFn: (vars: { channel: SubscriptionChannel; enabled: boolean }) => {
      // Para cada source da lista global, gerar 1 update (source/channel)
      const items: SubscriptionUpdate[] = PREFERENCE_SOURCES.map((src) => ({
        source: src,
        event_type: null,
        channel: vars.channel,
        enabled: vars.enabled,
      }))
      return notificationPreferencesApi.updateSubscriptions(items)
    },
    onSuccess: (_res, vars) => {
      invalidateMeNotificationQueries(qc)
      notifications.show({
        color: 'green',
        title: 'Salvo',
        message: `Canal ${vars.channel} ${vars.enabled ? 'ativado' : 'desativado'} para todas as fontes.`,
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: formatApiError(err, 'Falha ao atualizar preferência de canal.'),
      })
    },
  })

  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="teal" variant="light">
            <IconBellRinging size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Canais (master)</Text>
        </Group>

        <Callout variant="info">
          <Text size="xs">
            Liga/desliga um canal para <strong>todas as fontes</strong> de uma vez.
            Para configuração fina por fonte, use o card "Preferências por fonte" abaixo.
          </Text>
        </Callout>

        {isLoading ? (
          <Stack gap="xs">
            <Skeleton h={32} />
            <Skeleton h={32} />
            <Skeleton h={32} />
          </Stack>
        ) : (
          <Stack gap="xs">
            {PREFERENCE_MASTER_CHANNELS.map((ch) => (
              <Group key={ch.key} justify="space-between" align="center">
                <Text size="sm">{ch.label}</Text>
                <Switch
                  checked={masters[ch.key]}
                  disabled={mutation.isPending}
                  onChange={(e) =>
                    mutation.mutate({
                      channel: ch.key,
                      enabled: e.currentTarget.checked,
                    })
                  }
                  aria-label={`Master toggle ${ch.label}`}
                />
              </Group>
            ))}
            {mutation.isPending && (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="xs" c="dimmed">Salvando...</Text>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </WidgetCard>
  )
}
